import { Inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, CanActivateChild, CanLoad, Router, RouterStateSnapshot } from '@angular/router';
import { Store } from '@ngxs/store';
import { lastValueFrom } from 'rxjs';
import { API_URL, LOGIN_NEW_URL, LOGIN_URL } from 'src/app/services/app.constants';
import { CookieService } from 'src/app/services/cookie/cookie.service';
import { InvalidateUser, SetImpersonatePlan, UpdateCurrentUser } from 'src/app/store/auth/auth.actions';
import { AuthState } from 'src/app/store/auth/auth.state';
import { AppState } from 'src/app/store/state';

@Injectable({
  providedIn: 'root',
})
export class AuthGuard implements CanActivate, CanActivateChild, CanLoad {
  constructor(
    private store: Store,
    private router: Router,
    private cookieService: CookieService,
    @Inject(LOGIN_URL) private loginUrl,
    @Inject(LOGIN_NEW_URL) private loginNewUrl,
    @Inject(API_URL) private apiUrl: string,
  ) { }

  async canActivate(route?: ActivatedRouteSnapshot, state?: RouterStateSnapshot) {
    const token = this.store.selectSnapshot(AuthState.token);
    const adminLogin = this.store.selectSnapshot(AuthState.adminLogin);
    const cookieAuth = this.store.selectSnapshot(AuthState.cookieAuth);
    let redirectUrl = state?.url || undefined;

    // Check for redirectAPI in route params, or parse from state URL as fallback
    let redirectAPI = route?.queryParams?.redirectAPI;
    if (!redirectAPI && state?.url) {
      // Parse query params from URL string if not in route
      const urlTree = this.router.parseUrl(state.url);
      redirectAPI = urlTree.queryParams?.redirectAPI;
    }

    if (!token && !cookieAuth) {
      if (redirectUrl?.startsWith('/activate')) {
        redirectUrl = null;
      }

      // Preserve redirectAPI and redirectUrl parameters when redirecting to login
      const queryParams: string[] = [];

      if (redirectAPI) {
        queryParams.push('redirectAPI=' + encodeURIComponent(redirectAPI));
      }

      if (redirectUrl) {
        queryParams.push('redirectUrl=' + encodeURIComponent(redirectUrl));
      }

      const queryString = queryParams.length > 0 ? '?' + queryParams.join('&') : '';
      return this.router.parseUrl(this.loginUrl + queryString);
    }

    const user = this.store.selectSnapshot(AuthState.user);
    const userRequiresUpdate = this.store.selectSnapshot(((st: AppState) => st.auth.userRequiresUpdate));
    if (userRequiresUpdate || !user) {
      try {
        await lastValueFrom(this.store.dispatch(new UpdateCurrentUser()));
        if (adminLogin) await lastValueFrom(this.store.dispatch(new SetImpersonatePlan()));
      } catch (err) {
        if (err.error === 'Unauthorized' || err.error === 'invalidToken' || err.error === 'invalidCredentials' || err.error === 'expiredToken' || err.error === 'expiredRefreshToken' || err.error === 'denied') {
          await lastValueFrom(this.store.dispatch(new InvalidateUser()));
          return this.router.parseUrl(this.loginNewUrl);
        }
      }
    }

    // If user is authenticated and redirectAPI parameter exists, redirect to API endpoint
    if (redirectAPI) {
      // Remove API version segment (handles /api/{version}, /api/v1.0, /api/1.0, /api/version, etc.)
      const baseUrl = this.apiUrl.replace(/\/api\/[^\/]+/, '');

      // Get the access token from auth state and append it as query parameter
      // For v2 cookie-based auth, read the cookie value directly since AuthState.token is undefined
      let authToken = this.store.selectSnapshot(AuthState.token);

      if (!authToken && cookieAuth) {
        // For v2 cookie-based auth, read the cookie value directly from cookies
        authToken = this.cookieService.getCookie('__Host-accessToken') || this.cookieService.getCookie('accessToken');
      }

      const separator = redirectAPI.includes('?') ? '&' : '?';
      const fullRedirectUrl = baseUrl + redirectAPI + (authToken ? `${separator}token=${encodeURIComponent(authToken)}` : '');

      window.location.href = fullRedirectUrl;
      return false;
    }

    return true;
  }

  canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    return this.canActivate(route, state);
  }

  canLoad() {
    return this.canActivate(null, null).then(x => x === true);
  }
}
